/* eslint-disable no-unused-vars */
import PropTypes from 'prop-types';
import { useRef, useState, useContext } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, CardContent, ClickAwayListener, Grid, Paper, Popper, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { EditOutlined, UserOutlined, LogoutOutlined, SettingOutlined } from '@ant-design/icons';
import KeyIcon from '@mui/icons-material/Key';
import PsychologyIcon from '@mui/icons-material/Psychology';
import LockResetIcon from '@mui/icons-material/LockReset';
import WorkIcon from '@mui/icons-material/Work';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';

import Avatar from 'components/@extended/Avatar';
import MainCard from 'components/MainCard';
import Transitions from 'components/@extended/Transitions';
import IconButton from 'components/@extended/IconButton';
import AppStateContext from 'AppStateContext';

import { ThemeMode } from 'config';
import useAuth from 'hooks/useAuth';
import { SettingsOutlined } from '@mui/icons-material';
import { useAvatarColor } from 'contexts/AvatarColorContext';

// assets
// import avatar1 from 'assets/images/users/avatar-1.png';

// tab panel wrapper
function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`profile-tabpanel-${index}`} aria-labelledby={`profile-tab-${index}`} {...other}>
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
};

function a11yProps(index) {
  return {
    id: `profile-tab-${index}`,
    'aria-controls': `profile-tabpanel-${index}`
  };
}

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

const Profile = () => {
  const { currentUserDispatch, currentUserState } = useContext(AppStateContext);
  const userFirstName = currentUserState.user_first_name;
  // console.log('Profile > index.js: userFirstName: ' + userFirstName);
  const userLastName = currentUserState.user_last_name;
  const userAbbrName = userFirstName.charAt(0).toUpperCase() + userLastName;
  const userImage = currentUserState.user_image_url || '';
  const userEmail = currentUserState.user_email1;
  const theme = useTheme();
  const navigate = useNavigate();
  // console.log('currentUserState: ' + JSON.stringify(currentUserState));
  // console.log('HeaderContent Profile index.js: userImage: ' + userImage);
  const displayEmail = userEmail.length > 30 ? `${userEmail.substring(0, 28)}...` : userEmail;

  const { logout } = useAuth();
  const handleLogout = async () => {
    try {
      await logout();
      currentUserDispatch({
        type: 'CLEAR_CURRENT_USER'
      });
      navigate(`/`, {
        state: {
          from: ''
        }
      });
    } catch (err) {
      console.error(err);
    }
  };
  const avatarColor = useAvatarColor();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const iconBackColorOpen = theme.palette.mode === ThemeMode.DARK ? 'grey.200' : 'grey.300';

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <ButtonBase
        sx={{
          p: 0.25,
          bgcolor: open ? iconBackColorOpen : 'transparent',
          borderRadius: 1,
          '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.secondary.dark}`,
            outlineOffset: 2
          }
        }}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 0.5 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: avatarColor,
              color: 'white',
              mr: 2,
              mt: 0
            }}
            src={!userImage || userImage === 'null' ? '' : userImage}
          >
            {!userImage && (userFirstName ? userFirstName.charAt(0).toUpperCase() + userLastName.charAt(0).toUpperCase() : '?')}
          </Avatar>
          <Typography variant="subtitle1">{userAbbrName}</Typography>
        </Stack>
      </ButtonBase>
      <Popper
        placement="bottom-end"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 9]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
            <Paper
              sx={{
                boxShadow: theme.customShadows.z1,
                width: 290,
                minWidth: 240,
                maxWidth: 290,
                [theme.breakpoints.down('md')]: {
                  maxWidth: 250
                }
              }}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard elevation={0} border={false} content={false}>
                  <CardContent sx={{ px: 2.5, pt: 3 }}>
                    <Grid container justifyContent="space-between" alignItems="center">
                      <Grid item>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 38,
                              height: 38,
                              color: 'white',
                              backgroundColor: avatarColor,
                              mr: 2,
                              mt: 0
                            }}
                            src={userImage || ''}
                          >
                            {!userImage &&
                              (userFirstName ? userFirstName.charAt(0).toUpperCase() + userLastName.charAt(0).toUpperCase() : '?')}
                          </Avatar>
                          <Stack>
                            <Typography variant="h6">{userAbbrName}</Typography>
                            <Typography variant="body2" color="textSecondary">
                              {displayEmail}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                  <List component="nav" sx={{ p: 0, '& .MuiListItemIcon-root': { minWidth: 32 } }}>
                    <Link to="/UserProfile" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ListItemButton onClick={handleClose}>
                        <ListItemIcon>
                          <UserOutlined />
                        </ListItemIcon>
                        <ListItemText primary="User Settings" />
                      </ListItemButton>
                    </Link>
                    <Link to="/UserWork" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ListItemButton onClick={handleClose}>
                        <ListItemIcon>
                          <WorkIcon />
                        </ListItemIcon>
                        <ListItemText primary="Work Settings" />
                      </ListItemButton>
                    </Link>
                    <Link to="/UserAuth" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ListItemButton onClick={handleClose}>
                        <ListItemIcon>
                          <LockResetIcon />
                        </ListItemIcon>
                        <ListItemText primary="Authentication" />
                      </ListItemButton>
                    </Link>
                    <Link to="/UserVia" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ListItemButton onClick={handleClose}>
                        <ListItemIcon>
                          <PsychologyIcon />
                        </ListItemIcon>
                        <ListItemText primary="VIA Character" />
                      </ListItemButton>
                    </Link>
                    <Link to="/UserSubscription" style={{ textDecoration: 'none', color: 'inherit' }}>
                      <ListItemButton onClick={handleClose}>
                        <ListItemIcon>
                          <SubscriptionsIcon />
                        </ListItemIcon>
                        <ListItemText primary="Subscription" />
                      </ListItemButton>
                    </Link>
                    <ListItemButton onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutOutlined />
                      </ListItemIcon>
                      <ListItemText primary="Logout" />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
};

export default Profile;
